import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformServer, Location } from '@angular/common';
import { ActivatedRouteSnapshot, CanActivate, Params, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

import { isNull, LocalDate } from '@fcom/core/utils';
import { LanguageService } from '@fcom/ui-translate';
import { SentryLogger, GlobalBookingTravelClass, ConfigService } from '@fcom/core';
import { Cabin } from '@fcom/dapi/api/models';
import { FinnairMultiCitySearchInterface } from '@fcom/common/interfaces';
import { BOOKING_STEPS } from '@fcom/common/config/booking-config';

import { BookingService } from '../../../services';
import { UTM_PARAMS } from '../../../../core/constants';

const PROMO_CODE_REGEXP = /^[A-Z0-9\-_]{2,15}$/;

const validate = {
  flights: (flights: unknown, isMultiCityEnabled: boolean): true | string => {
    const maxBounds = isMultiCityEnabled ? 6 : 2;

    if (!flights || !Array.isArray(flights) || flights.length === 0 || flights.length > maxBounds) {
      return 'flights invalid';
    }

    const flightsAreValid = flights
      .map((flightSearch, i) => {
        if (!validate.locationCode(flightSearch.origin)) {
          return `flight ${i} origin invalid`;
        }

        if (!validate.locationCode(flightSearch.destination)) {
          return `flight ${i} destination invalid`;
        }

        if (!validate.date(flightSearch.departureDate)) {
          return `flight ${i} departureDate invalid`;
        }

        if (i === 0 && new LocalDate(flightSearch.departureDate).lt(LocalDate.now())) {
          return `flight ${i} departureDate is in the past invalid`;
        }

        if (i === 1 && new LocalDate(flightSearch.departureDate).lt(new LocalDate(flights[0].departureDate))) {
          return `flight ${i} departureDate is earlier that the outbound departureDate`;
        }

        if (!validate.fareFamily(flightSearch.fareFamily)) {
          return `flight ${i} fareFamily invalid`;
        }

        if (!validate.flightNumbers(flightSearch.flightNumbers)) {
          return `flight ${i} flightNumbers invalid`;
        }

        return true;
      })
      .find((validated) => typeof validated === 'string');

    if (typeof flightsAreValid === 'string') {
      return flightsAreValid;
    }

    return true;
  },
  locationCode: (code: unknown): boolean => {
    return typeof code === 'string' && code.length === 3;
  },
  date: (date: unknown): boolean => {
    return typeof date === 'string' && LocalDate.isLocalDate(date);
  },
  fareFamily: (fareFamily: unknown): boolean => {
    if (typeof fareFamily === 'undefined') {
      return true;
    }

    return typeof fareFamily === 'string' && fareFamily.length < 20;
  },
  flightNumbers: (flightNumbers: unknown): boolean => {
    if (typeof flightNumbers === 'undefined') {
      return true;
    }

    if (!Array.isArray(flightNumbers) || flightNumbers.length > 9) {
      return false;
    }

    return Boolean(
      flightNumbers
        .map((flightNumber: unknown) => {
          return typeof flightNumber === 'string' && flightNumber.length < 10;
        })
        .filter(Boolean).length
    );
  },
  cabin: (cabin: unknown): true | string => {
    const isValid =
      typeof cabin === 'string' &&
      Object.keys(GlobalBookingTravelClass)
        .map((k) => GlobalBookingTravelClass[k])
        .indexOf(cabin) !== -1;

    return isValid || `cabin ${cabin} invalid`;
  },
  paxAmount: (search: FinnairMultiCitySearchInterface): true | string => {
    // Check that all the different paxTypes are either undefined or numerical <= 9
    const basicCheck = ['adults', 'children', 'infants', 'c15s', 'b15s'].map((paxType) => {
      const amount: unknown = search[paxType];
      const isOk = typeof amount === 'undefined' || (typeof amount === 'number' && amount <= 9);

      return isOk || `pax type ${paxType} with value ${amount} invalid`;
    });
    const firstFail = basicCheck.find((check) => typeof check === 'string');

    if (firstFail) {
      return firstFail;
    }

    if (search.adults < 1) {
      return 'no adults check invalid';
    }

    if (search.children || search.c15s) {
      if (search.children + search.c15s >= 0 && search.children + search.c15s > 9 - search.adults) {
        return `more children+c15s (${search.children + search.c15s}) than adults (${search.adults}) check invalid`;
      }
    }

    if (search.infants) {
      if (search.infants > search.adults) {
        return `more infants (${search.infants}) than adults (${search.adults}) check invalid`;
      }
    }

    return true;
  },
  directFlights: (directFlights: unknown): true | string => {
    const isValid = typeof directFlights === 'undefined' || typeof directFlights === 'boolean';

    return isValid || 'directFlights not a boolean';
  },
  isAward: (isAward: unknown): true | string => {
    const isValid = typeof isAward === 'undefined' || isNull(isAward) || typeof isAward === 'boolean';

    return isValid || 'isAward not a boolean';
  },
  promoCode: (promoCode: unknown): true | string => {
    const isValid =
      typeof promoCode === 'undefined' || (typeof promoCode === 'string' && PROMO_CODE_REGEXP.test(promoCode));

    return isValid || `promoCode is invalid: ${promoCode}`;
  },
};

const getQueryParameters = (route: ActivatedRouteSnapshot): FinnairMultiCitySearchInterface => {
  const params = JSON.parse(decodeURIComponent(route.queryParams.json));

  return {
    cabin: Cabin.MIXED,
    ...params,
  };
};

@Injectable()
export class TicketSelectionGuard implements CanActivate {
  constructor(
    private bookingService: BookingService,
    private router: Router,
    private languageService: LanguageService,
    private sentryLogger: SentryLogger,
    private configService: ConfigService,
    private location: Location,
    @Inject(PLATFORM_ID) private platform: object
  ) {}

  isOldDeeplinkFormat(queryParams: Params): boolean {
    return Boolean(queryParams.origin && queryParams.destination);
  }

  redirectToJsonDeeplink({ queryParams }: { queryParams: Params }): UrlTree | boolean {
    try {
      const search: FinnairMultiCitySearchInterface = {
        flights: [],
      };

      search.flights.push({
        origin: queryParams.origin,
        destination: queryParams.destination,
        departureDate: queryParams.departureDate,
      });

      if (queryParams.departureFlightNumbers) {
        search.flights[0].flightNumbers = queryParams.departureFlightNumbers.split(',');
      }

      if (queryParams.departureFareFamily) {
        search.flights[0].fareFamily = queryParams.departureFareFamily;
      }

      if (queryParams.returnDate) {
        search.flights.push({
          origin: queryParams.destination,
          destination: queryParams.origin,
          departureDate: queryParams.returnDate,
        });
      }

      if (queryParams.returnFlightNumbers) {
        search.flights[1].flightNumbers = queryParams.returnFlightNumbers.split(',');
      }

      if (queryParams.returnFareFamily) {
        search.flights[1].fareFamily = queryParams.returnFareFamily;
      }

      ['adults', 'b15s', 'c15s', 'children', 'infants'].forEach((paxType) => {
        if (queryParams[paxType]) {
          search[paxType] = Number(queryParams[paxType]);
        }
      });

      if (queryParams.cabin) {
        search.cabin = queryParams.cabin;
      }

      if (queryParams.directFlights) {
        search.directFlights = queryParams.directFlights === 'true';
      }

      if (queryParams.isAward) {
        search.isAward = queryParams.isAward === 'true';
      }

      if (queryParams.promoCode) {
        search.promoCode = queryParams.promoCode;
      }

      const urlTree = this.router.createUrlTree(
        [this.languageService.langValue, 'booking', BOOKING_STEPS.FLIGHT_SELECTION.path],
        {
          queryParams: {
            json: JSON.stringify(search),
            ...this.filterUtmParams(queryParams),
          },
        }
      );

      // We want to replace the history with the json url to avoid quaryParams issues
      // when the user would press the browser's the back button etc.
      const jsonUrl = this.router.serializeUrl(urlTree);
      this.location.replaceState(jsonUrl);

      return urlTree;
    } catch (e) {
      this.sentryLogger.error('Error converting to JSON deeplink format', {
        error: queryParams,
      });
      return false;
    }
  }

  filterUtmParams(queryParams: Params): Params {
    return Object.keys(queryParams)
      .filter((key) => UTM_PARAMS.includes(key))
      .reduce(
        (params, key) => ({
          ...params,
          [key]: queryParams[key],
        }),
        {}
      );
  }

  validateDeeplink(search: FinnairMultiCitySearchInterface): boolean {
    const validations = [];

    validations.push(validate.flights(search.flights, this.configService.cfg.enableMultiCity));
    validations.push(validate.cabin(search.cabin));
    validations.push(validate.paxAmount(search));
    validations.push(validate.directFlights(search.directFlights));
    validations.push(validate.isAward(search.isAward));
    validations.push(validate.promoCode(search.promoCode));

    const passesValidation = validations.length === validations.filter((validation) => validation === true).length;
    /**
     * Specifically ignore this departureDate case because it happens a lot from customers reusing old links, and this check is
     * not a valid sentry issue for us to investigate.
     */
    if (!passesValidation && !validations.includes('flight 0 departureDate is in the past invalid')) {
      this.sentryLogger.info('validation failed for deeplink', {
        search,
        validations: validations.filter((validation) => typeof validation === 'string'),
      });
    }

    return passesValidation;
  }

  hasJSONDeeplink(route: ActivatedRouteSnapshot): boolean {
    return Boolean(route.queryParams.json);
  }

  canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): boolean | UrlTree {
    if (isPlatformServer(this.platform)) {
      return true;
    }

    if (this.isOldDeeplinkFormat(route.queryParams)) {
      return this.redirectToJsonDeeplink(route);
    }

    try {
      /**
       * Avoid trying to process if there is no deeplink. This also avoids any extra sentry alerts
       * for this quite common case (logout from pax details page is one situation).
       **/
      if (!this.hasJSONDeeplink(route)) {
        this.bookingService.setStep({ step: BOOKING_STEPS.ENTRY });
        return false;
      }

      const search = getQueryParameters(route);
      const canAccess = this.validateDeeplink(search);

      if (!canAccess) {
        this.bookingService.setStep({ step: BOOKING_STEPS.ENTRY });
      }

      return true;
    } catch (e) {
      this.sentryLogger.error('Uncaught error reading and validating deeplink', {
        error: route.queryParams,
      });
      return false;
    }
  }
}
